/**
 * End screen overlay (HANDOFF §6 Agent E / §3.5 / §8).
 *
 * Shown when `state.phase === 'final'`. Displays the verdict from `state.winner`
 * (Victory / Defeat / Dead heat) and the final golf cards (scores), with a
 * "Rematch" button that calls `onRematch()`. DOM/CSS with §8 tokens.
 */
import type { GameState, Winner } from '../sim/types.ts';

export interface EndScreenDeps {
  onRematch(): void;
}

export interface EndScreen {
  /** Reflect the decided match into the overlay and reveal it. */
  show(state: GameState): void;
  /** Show (or clear with null) a reward line, e.g. coins earned for a win. */
  setReward(text: string | null): void;
  hide(): void;
  readonly el: HTMLElement;
}

/** Format a strokes total as golf does: E for even, signed otherwise (§3.5). */
export function formatScore(n: number): string {
  if (n === 0) return 'E';
  return n > 0 ? `+${n}` : `${n}`;
}

function verdictText(winner: Winner | null): { text: string; good: boolean } {
  if (winner === 'you') return { text: 'Victory', good: true };
  if (winner === 'rival') return { text: 'Defeat', good: false };
  return { text: 'Dead heat', good: true }; // 'tie' or null
}

/**
 * Build the end overlay under `root` (hidden until `show`). `show(state)` reads
 * `state.winner` and both sides' scores; the verdict colour follows §8 (good =
 * turf-lit, bad = flag).
 */
export function createEndScreen(root: HTMLElement, deps: EndScreenDeps): EndScreen {
  const el = document.createElement('section');
  el.className = 'fc-overlay fc-overlay--end';
  el.style.display = 'none';

  const verdict = document.createElement('div');
  verdict.className = 'fc-verdict';

  const line = document.createElement('p');
  line.className = 'fc-overlay__blurb fc-end__line';

  // Reward line (coins earned on a win) — populated async after the server grant; hidden otherwise.
  const reward = document.createElement('p');
  reward.style.cssText =
    'display:none;font-family:var(--font-pixel,monospace);font-size:11px;color:var(--gold,#f2c14e);margin:2px 0 4px;';

  const again = document.createElement('button');
  again.type = 'button';
  again.className = 'fc-btn';
  again.textContent = 'Rematch';
  again.addEventListener('click', () => deps.onRematch());

  el.append(verdict, line, reward, again);
  root.appendChild(el);

  function setReward(text: string | null): void {
    if (text === null) {
      reward.style.display = 'none';
      return;
    }
    reward.textContent = text;
    reward.style.display = 'block';
  }

  function show(state: GameState): void {
    const v = verdictText(state.winner);
    verdict.textContent = v.text;
    verdict.classList.toggle('is-win', v.good);
    verdict.classList.toggle('is-lose', !v.good);

    const you = formatScore(state.you.score);
    const rival = formatScore(state.rival.score);
    line.innerHTML =
      `Final card — You <b class="fc-good">${you}</b>` +
      ` · Rival <b class="fc-bad">${rival}</b>`;

    setReward(null); // reset; the host sets it after the win-reward grant resolves
    el.style.display = 'flex';
  }

  return {
    el,
    show,
    setReward,
    hide() {
      el.style.display = 'none';
    },
  };
}
